<template>
    <div>
        <div :key="variant.id" class="custom-control custom-checkbox" v-for="variant of answer.variants">
            <input :id="`MultipleItemsAnswer${questionId}-${variant.id}`" :value="variant.id"
                   class="custom-control-input" type="checkbox" v-model="variantsIds" :disabled="isReadOnly">
            <label :for="`MultipleItemsAnswer${questionId}-${variant.id}`"
                   class="custom-control-label">{{variant.text}}</label>
        </div>
<!--         <div class="custom-control custom-checkbox">
            <input :id="`MultipleItemsAnswer${questionId}-none`"
                   class="custom-control-input" type="checkbox" v-model="noOptions" :disabled="isReadOnly">
            <label :for="`MultipleItemsAnswer${questionId}-none`"
                   class="custom-control-label">None of the options</label>
        </div> -->
        
        <div class="mt-4 " v-if="isUser">
            <button class="btn btn-primary btn-saved" @click="onAnswerChanged">
                Save
            </button>

            <report-button 
                questionText="Do you want to report an error?"
                callToActionText="Write us!"
                @click.native="showModal"
            />
        </div>

        <feedback-modal 
            :modalVisible.sync="modalVisible"
            :answers.sync="answers" 
            :treatmentHistory.sync="treatmentHistory"
            :excludedRecommendations.sync="excludedRecommendations"
            :branchId.sync="branchId"
        />

        <saving-warning-modal 
            type="multi"
            :warningModalVisible.sync="warningModalVisible" 
            :dependentQuestionNames.sync="dependentQuestionNames"
            :questionId="questionId"
            :variantsIds="variantsIds"
            :variantsTexts="variantsTexts"
            :localGroups="localGroups"
            :localQuestions="localQuestions"
        />
    </div>
</template>

<script lang="ts">
import {Component, Prop, Vue, Watch} from "vue-property-decorator";
import constants from "@/entities/constants";
import {MultipleItemsAnswer} from "@/entities/questionaire/answers/MultipleItemsAnswer";
import ReportButton from '../../UI/ReportButton.vue';
import FeedbackModal from '../../UI/FeedbackModal.vue';
import { Answer } from "@/entities/questionaire/answers/Answer";
import { Treatment } from "@/entities/Treatment";
import { Property } from "@/entities/questionaire/Property";
import { Group } from "@/entities/questionaire/Group";
import SavingWarningModal from "@/components/UI/SavingWarningModal.vue";

@Component({
    name: constants.answerTypes.MultipleItemsAnswer.componentName,
    components: {
        ReportButton,
        FeedbackModal,
        SavingWarningModal,
    },
})
export default class AppMultipleItemsAnswer extends Vue {
    public variantsIds: number[] = [];
    public variantsTexts: string[] = [];
    public noOptions: boolean = false;
    modalVisible: boolean = false;
    warningModalVisible: boolean = false;
    dependentQuestionNames: string[] = [];

    @Prop()
    questionId!: number;

    @Prop()
    answer!: MultipleItemsAnswer;

    @Prop()
    selectedAnswer!: MultipleItemsAnswer;

    @Prop()
    isReadOnly!: boolean;

    @Prop()
    isUser!: boolean;

    @Prop()
    answers!: Answer[];

    @Prop()
    treatmentHistory!: Treatment[] | null;

    @Prop()
    branchId!: number | null;

    @Prop()
    property!: Property;

    @Prop()
    public localGroups!: Group[];

    @Prop()
    localQuestions!: Property[];

    @Prop()
    excludedRecommendations!: {
        id: number;
        name: string;
        approvalPercentage: number;
    }[] | [];

    @Watch("variantsIds")
    onVariantsIdsChanged() {
        if (this.variantsIds.length > 0)
            this.noOptions = false;

        this.variantsTexts = this.answer.variants.filter(a => this.variantsIds.includes(a.id)).map(ans => ans.text);

        if (!this.isUser){
            this.onAnswerChanged();
        }
    }

    created() {
        if (this.selectedAnswer && this.selectedAnswer.answers) {
            this.variantsIds = this.selectedAnswer.answers;
        }
    }

    @Watch("noOptions")
    onNoOptionsChanged() {
        if (this.noOptions) {
            this.variantsIds = [];
            this.variantsTexts = [];
        }
        else {
            this.variantsTexts = this.answer.variants.filter(a => this.variantsIds.includes(a.id)).map(ans => ans.text);
            if (!this.isUser){
                this.onAnswerChanged();
            }
        }
    }

    getAllDependentQuestionNames(id: number) {
        for (let i = 0; i < this.localQuestions.length; i++) {
            if (this.localQuestions[i].questionConditionId === id) {
                this.dependentQuestionNames.push(this.localQuestions[i].question);
                this.getAllDependentQuestionNames(this.localQuestions[i].id);
            }
        }
        return this.dependentQuestionNames;
    }

    checkDependentQuestion(question: Property) {
        const subDepQ = this.localQuestions.filter(q => q.questionConditionId === question.id);
        if (subDepQ.length > 0) {
            for (let i = 0; i < subDepQ.length; i++) {
                subDepQ[i].isHidden = true;
                this.checkDependentQuestion(subDepQ[i]);
            }
        }
    }

    onAnswerChanged() {
        if (!this.noOptions && this.variantsIds.length < 1)  {
            this.$events.emit("answerChanged", new MultipleItemsAnswer(this.questionId, []));
        } else {
            const dependentQuestions = this.localQuestions
                .filter(question => question.questionConditionId === this.property.id)
                .map(question => question.id);

            if (dependentQuestions.length > 0) {
                const existingAnswer = this.answers.find(answer => answer.PropertyId === this.property.id);

                if (existingAnswer) {
                    this.dependentQuestionNames = this.getAllDependentQuestionNames(this.property.id);
                    this.warningModalVisible = true;
                } else {
                    for (let j = 0; j < dependentQuestions.length; j++) {
                        const dependentQuestion = this.localQuestions.find(q => q.id === dependentQuestions[j]);
                        const answers = dependentQuestion?.answerQuestions
                            ?.filter(prop => this.variantsIds.includes(Number(prop)));

                        if (
                            (answers && answers.length > 0 && dependentQuestion?.conditionQuestion === 'notIn') ||
                            (answers && answers.length === 0 && dependentQuestion?.conditionQuestion === 'in')
                        ) {
                            dependentQuestion.isHidden = true;
                            this.checkDependentQuestion(dependentQuestion);
                        }
                    }
                    
                    this.$events.emit("answerChanged", new MultipleItemsAnswer(this.questionId, [], this.variantsIds, this.variantsTexts));
                }
            } else {
                this.$events.emit("answerChanged", new MultipleItemsAnswer(this.questionId, [], this.variantsIds, this.variantsTexts));
            }
        }
    }

    showModal() {
        this.modalVisible = true;
    }
}
</script>

<style lang="scss" scoped>

.btn-saved {
    background: white;
    color: #2f45a5;
    font-weight: 600;
    height: 36px;
    padding: 0 1rem;
    border-radius: 8px;
    border: 2px solid rgba(47, 69, 165, 0.35);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    transition: all 0.3s ease;
    cursor: pointer;
    font-size: 0.875rem;
    display: flex;
    align-items: center;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

.btn-saved:not(:disabled):hover {
    background: linear-gradient(135deg, #2f45a5, #667bf2);
    border-color: transparent;
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(47, 69, 165, 0.25);
}

.btn-saved:active:not(:disabled) {
    transform: translateY(0);
}

.btn-saved:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.btn-saved:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(102, 123, 242, 0.2);
}

.action-btns {
    display: flex;
}

@media only screen and (max-width: 768px) {
    .custom-control {
        padding-left: 1.5rem;
        min-height: 1.25rem;
        margin-bottom: 0.35rem;
    }

    .custom-control-label {
        font-size: 0.875rem;
        line-height: 1.25rem;
        padding-top: 0;

        &::before,
        &::after {
            width: 1rem;
            height: 1rem;
            top: 0.1rem;
        }
    }
}

@media only screen and (max-width: 599px) {
    .action-btns {
        flex-flow: column;
    }
}

@media only screen and (min-width: 600px) {
    .action-btns {
        flex-flow: row;
        justify-content: space-between;
    }
}

@media only screen and (min-width: 768px) {
    .action-btns {
        flex-flow: column;
    }
}

@media only screen and (min-width: 992px) {
    .action-btns {
        flex-flow: row;
        justify-content: space-between;
    }

    .btn-saved {
        height: 44px;
        padding: 0 1.75rem;
        font-size: 1rem;
    }
}
</style>
